<script setup lang="ts" generic="T extends Record<string, unknown>">
import type { PaginationProps } from 'naive-ui'

const props = defineProps<{
  data: T[]
  actionButtons?: ((row: T) => CustomerModuleAction[])
  columns: OneDataColumn<T>[]
  controlledPaginationSettings?: PaginationProps
  getContainerClass?: (row: T) => string
  getActionButtonClass?: (action: CustomerModuleAction, row: T) => string
  placeholderSize?: 'normal' | 'small'
}>()

const emit = defineEmits<{
  (e: CustomerModuleAction, entity: T): void
  (e: 'update:page', page: number): void
  (e: 'update:page-size', page: number): void
}>()

// Default pagination settings
const pageSize = ref(3)
const page = ref(1)

const paginatedData = computed(() => props.controlledPaginationSettings
  ? props.data
  : props.data.slice((page.value - 1) * pageSize.value, page.value * pageSize.value,
    ))

const paginationProps = computed(() => (props.controlledPaginationSettings || {
  total: props.data.length,
  pageCount: Math.ceil(props.data.length / pageSize.value),
  pageSize: pageSize.value,
  page: page.value,
  pageSizes: [3, 5],
  showSizePicker: true,
}))

function updatePage(newPage: number) {
  if (props.controlledPaginationSettings) {
    emit('update:page', newPage)
  } else {
    page.value = newPage
  }
}

function updatePageSize(newSize: number) {
  if (props.controlledPaginationSettings) {
    emit('update:page-size', newSize)
  } else {
    pageSize.value = newSize
  }
}
</script>

<template>
  <CustomerModuleEmptyPlaceholder
    v-if="data.length === 0"
    :title="$t('customerModule.empty.title')"
    :size="placeholderSize"
    icon-name="material-symbols:inbox-outline-rounded"
  />

  <div v-else class="flex flex-col gap-3">
    <div
      v-for="(item, index) in paginatedData"
      :key="index"
      class="flex flex-col gap-3 rounded border"
      :class="getContainerClass ? getContainerClass(item) : ''"
    >
      <div class="p-4" :class="actionButtons && 'pb-0'">
        <TableOneDataColumn :config="{ data: item, columns }" class="text-base" />
      </div>

      <div v-if="actionButtons" class="flex flex-wrap items-center gap-2 bg-gray-50 px-4 py-3 sm:py-2">
        <n-button
          v-for="action in actionButtons(item)"
          :key="action"
          secondary
          :class="getActionButtonClass ? getActionButtonClass(action, item) : ''"
          @click="emit(action, item)"
        >
          <template #icon>
            <Icon :name="actionButtonIconPresets[action]" />
          </template>
          {{ $t(`customerModule.actionButton.${action}`) }}
        </n-button>
      </div>
    </div>
    <n-pagination
      v-bind="paginationProps"
      class="flex flex-wrap justify-end gap-y-2"
      @update:page-size="updatePageSize"
      @update:page="updatePage"
    />
  </div>
</template>
